
import React from 'react';
import { ExternalLink } from 'lucide-react';
import LogoIcon from './icons/LogoIcon';
import FacebookIcon from './icons/FacebookIcon';
import InstagramIcon from './icons/InstagramIcon';
import XIcon from './icons/XIcon';
import { t } from '../translations';

const Footer: React.FC<{ isHomePage?: boolean }> = ({ isHomePage = true }) => {
  const companyLinks = [
    { href: '/services/', label: t.header.services, sectionId: 'services' },
    { href: '/about/', label: t.header.about, sectionId: 'about' },
    { href: '/case-studies/', label: t.header.caseStudies, sectionId: 'case-studies' },
    { href: '/blog/', label: 'Blog', sectionId: null },
    { href: isHomePage ? '#faq' : '/#faq', label: t.header.faq, sectionId: 'faq' },
    { href: '/contact/', label: t.header.contact, sectionId: 'contact' },
  ];

  const webApps = [
    { href: 'https://studio.82marketing.com', label: "82 Marketing Studio", badge: "AI" },
    { href: 'https://82leads.com/', label: "82 Leads", badge: "Leads" },
    { href: 'https://flightdeck.boo', label: "FlightDeck.boo", badge: "Ops" },
    { href: 'https://cataboost.com', label: "CataBoost.com", badge: "Feed" },
  ];

  const mobileApps = [
    { href: 'https://mazarion.com/', label: "Mazarion", badge: "Mobile App" }
  ];

  const handleSectionClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string, label: string, sectionId?: string | null) => {
    window.dataLayer?.push({
      event: 'navigation_click',
      link_text: label,
      link_url: href,
      link_location: 'footer_nav'
    });
    if (isHomePage && sectionId) {
      const el = document.getElementById(sectionId);
      if (el) {
        e.preventDefault();
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleAppClick = (appName: string, url: string, category: 'web_apps' | 'mobile_apps') => {
    window.dataLayer?.push({
      event: 'outbound_link_click',
      link_text: appName,
      link_url: url,
      link_category: category,
      link_location: 'footer'
    });
  };

  const handleSocialClick = (platform: string, url: string) => {
    window.dataLayer?.push({
      event: 'social_click',
      platform: platform,
      link_url: url
    });
  };

  const handleContactClick = (type: string, value: string) => {
    window.dataLayer?.push({
      event: 'contact_info_click',
      contact_type: type,
      contact_value: value,
      contact_location: 'footer'
    });
  };

  return (
    <footer className="bg-gray-900 text-gray-400">
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 text-center sm:text-left">
          {/* Column 1: Brand & Social */}
          <div>
            <a 
              href="/" 
              onClick={(e) => {
                if (isHomePage) {
                  e.preventDefault();
                  document.getElementById('home')?.scrollIntoView({ behavior: 'smooth' });
                }
                handleSectionClick(e, '/', 'Footer Logo', 'home');
              }} 
              className="inline-flex transition-transform hover:scale-105"
              aria-label="82 Marketing home"
            >
              <LogoIcon className="text-primary-400 h-10 w-10" />
            </a>
            <p className="text-gray-400 mt-4 max-w-xs mx-auto sm:mx-0 text-sm leading-relaxed">
              {t.footer.description}
            </p>
            <div className="flex justify-center sm:justify-start gap-4 mt-6">
              <a 
                href="https://x.com/82_marketing" 
                target="_blank" 
                rel="noreferrer" 
                onClick={() => handleSocialClick('X', 'https://x.com/82_marketing')}
                className="hover:text-primary-400 transition-colors"
                aria-label="X (Twitter)"
              >
                <XIcon />
              </a>
              <a 
                href="https://www.facebook.com/82marketing" 
                target="_blank" 
                rel="noreferrer" 
                onClick={() => handleSocialClick('Facebook', 'https://www.facebook.com/82marketing')}
                className="hover:text-primary-400 transition-colors"
                aria-label="Facebook"
              >
                <FacebookIcon />
              </a>
              <a 
                href="https://www.instagram.com/82marketing" 
                target="_blank" 
                rel="noreferrer" 
                onClick={() => handleSocialClick('Instagram', 'https://www.instagram.com/82marketing')}
                className="hover:text-primary-400 transition-colors"
                aria-label="Instagram"
              >
                <InstagramIcon />
              </a>
            </div>
          </div>

          {/* Column 2: Company Navigation */}
          <div>
            <h3 className="font-bold text-white uppercase text-sm tracking-wider">Company</h3>
            <nav className="mt-4 flex flex-col space-y-2">
              {companyLinks.map((link) => (
                <a 
                  key={link.href} 
                  href={link.href} 
                  onClick={(e) => handleSectionClick(e, link.href, link.label, link.sectionId)} 
                  className="hover:text-primary-400 transition-colors text-sm"
                >
                  {link.label}
                </a>
              ))}
            </nav>
          </div>

          {/* Column 3: Web Apps & Mobile Apps */}
          <div>
            <div>
              <h3 className="font-bold text-white uppercase text-sm tracking-wider flex items-center justify-center sm:justify-start gap-2">
                <span>Web Apps</span>
                <span className="text-[10px] bg-gray-800 text-primary-400 px-1.5 py-0.5 rounded font-normal">SaaS</span>
              </h3>
              <nav className="mt-3 flex flex-col space-y-2">
                {webApps.map((app) => (
                  <a 
                    key={app.href} 
                    href={app.href} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    onClick={() => handleAppClick(app.label, app.href, 'web_apps')} 
                    className="hover:text-primary-400 transition-colors text-sm flex items-center justify-center sm:justify-start gap-2 group"
                  >
                    <span>{app.label}</span>
                    <span className="text-[9px] px-1.5 py-0.2 bg-gray-800 text-gray-300 rounded font-semibold group-hover:text-primary-400 transition-colors">
                      {app.badge}
                    </span>
                    <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                ))}
              </nav>
            </div>

            <div className="mt-6 pt-5 border-t border-gray-800">
              <h3 className="font-bold text-white uppercase text-sm tracking-wider flex items-center justify-center sm:justify-start gap-2">
                <span>Mobile Apps</span>
                <span className="text-[10px] bg-amber-950/60 text-amber-400 px-1.5 py-0.5 rounded font-normal">iOS & Android</span>
              </h3>
              <nav className="mt-3 flex flex-col space-y-2">
                {mobileApps.map((app) => (
                  <a 
                    key={app.href} 
                    href={app.href} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    onClick={() => handleAppClick(app.label, app.href, 'mobile_apps')} 
                    className="hover:text-amber-400 transition-colors text-sm flex items-center justify-center sm:justify-start gap-2 group text-amber-200/90"
                  >
                    <span className="font-medium">{app.label}</span>
                    <span className="text-[9px] px-1.5 py-0.2 bg-amber-900/40 text-amber-300 rounded font-semibold">
                      Self-Discovery
                    </span>
                    <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                ))}
              </nav>
            </div>
          </div>

          {/* Column 4: Contact Details */}
          <div>
            <h3 className="font-bold text-white uppercase text-sm tracking-wider">{t.footer.contactDetails}</h3>
            <div className="mt-4 flex flex-col space-y-2 text-sm">
              <a 
                href="mailto:hello@82marketing.com" 
                onClick={() => handleContactClick('email', 'hello@82marketing.com')}
                className="hover:text-primary-400 transition-colors"
              >
                hello@82marketing.com
              </a>
              <a 
                href="tel:+17174507164" 
                onClick={() => handleContactClick('phone', '+1 (717) 450-7164')}
                className="hover:text-primary-400 transition-colors"
              >
                +1 (717) 450-7164
              </a>
              <div className="pt-2">
                <p className="font-semibold text-gray-300">{t.contact.info.addressCompany}</p>
                <p>{t.contact.info.addressLocation}</p>
                <p className="text-xs text-gray-400 mt-1">{t.contact.info.addressRegistered}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 border-t border-gray-800 pt-6 text-center text-gray-400 text-sm">
          <p>&copy; {new Date().getFullYear()} 82 Marketing. {t.footer.rights}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
